import * as React from 'react';
import type { AssetId } from 'owa-timestream-types';
import { pointerMove, pointerUp } from '../actions';
import { usePointerEvents } from 'owa-react-hooks/lib/usePointerEvents';

/**
 * Tracks the position of the pointer relative to the page.
 * This will raise the pointerMove and pointerUp events with the
 * delta between the corresponding event and the pointer down position.
 */
export default function useMousePosition() {
    const usingPointerEvents = usePointerEvents();
    React.useEffect(() => {
        document.body.style.touchAction = 'none';

        if (usingPointerEvents) {
            document.addEventListener('pointerdown', onPointerDown, true);
        } else {
            document.addEventListener('mousedown', onMouseDown, true);
        }

        return () => {
            if (usingPointerEvents) {
                document.removeEventListener('pointerdown', onPointerDown, true);
            } else {
                document.removeEventListener('mousedown', onMouseDown, true);
            }
        };
    }, [usingPointerEvents]);
}

// Record the position of the pointer at pointerdown, so we can report
// just the delta on pointerMove. No need to use satcheljs store for this.
let pointerType: string | undefined = undefined;
let pointerId: number | undefined = undefined;
let pointerDownX = 0;
let pointerDownY = 0;

// NOTE: we handle pointerdown and pointermove in the capturing phase,
// before other components have a chance to handle it, but we handle
// pointerup after everybody else, giving them a chance to inspect deltas.
// Also notice this captures the pointer on the document, so we can report drags outside the window.
function onPointerDown(e: PointerEvent) {
    if (e.isPrimary) {
        pointerType = e.pointerType;
        pointerId = e.pointerId;
        pointerDownX = e.pageX;
        pointerDownY = e.pageY;
        document.addEventListener('pointermove', onPointerMove, true);
        document.addEventListener('pointerup', onPointerUp, false);
    }
}

function onPointerMove(e: PointerEvent) {
    if (isExpectedPointer(e)) {
        onMouseMove(e);
    }
}

function onPointerUp(e: PointerEvent) {
    if (isExpectedPointer(e)) {
        document.removeEventListener('pointermove', onPointerMove, true);
        document.removeEventListener('pointerup', onPointerUp, false);
        raisePointerUp(e);
    }
}

function isExpectedPointer(e: PointerEvent) {
    return e.isPrimary && e.pointerType == pointerType && e.pointerId == pointerId;
}

function onMouseDown(e: MouseEvent) {
    pointerDownX = e.pageX;
    pointerDownY = e.pageY;
    document.addEventListener('mousemove', onMouseMove, true);
    document.addEventListener('mouseup', onMouseUp, false);
}

function onMouseMove(e: MouseEvent) {
    pointerMove(e.pageX - pointerDownX, e.pageY - pointerDownY);
}

function onMouseUp(e: MouseEvent) {
    document.removeEventListener('mousemove', onMouseMove, true);
    document.removeEventListener('mouseup', onMouseUp, false);
    raisePointerUp(e);
}

function raisePointerUp(e: PointerEvent | MouseEvent) {
    const dataset = (e.target as HTMLElement)?.dataset;
    const targetMinOrderOrNaN = parseInt(dataset?.targetMinOrder || '');
    const targetMaxOrderOrNaN = parseInt(dataset?.targetMaxOrder || '');
    pointerUp(
        dataset?.bucketId as AssetId | undefined,
        isNaN(targetMinOrderOrNaN) ? undefined : targetMinOrderOrNaN,
        isNaN(targetMaxOrderOrNaN) ? undefined : targetMaxOrderOrNaN
    );
}
